<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="파트너스 포인트"
        subtitle="포인트 충전 및 내역 관리"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 잔액 + 바로가기 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 잔액 카드 -->
            <div class="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white">
              <div class="flex items-center gap-2 mb-1">
                <div class="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <span class="text-[9px] font-black text-white">PP</span>
                </div>
                <p class="text-blue-100 text-sm">파트너스 포인트</p>
              </div>
              <div class="flex items-end gap-2 mb-4">
                <span class="text-3xl font-bold">{{ balance.toLocaleString() }}</span>
                <span class="text-blue-200 mb-0.5">P</span>
              </div>
              <button
                @click="showChargeModal = true"
                class="w-full px-4 py-2.5 bg-white/20 hover:bg-white/30 rounded-xl font-medium transition text-sm"
              >
                충전하기
              </button>
            </div>

            <!-- 계좌 정보 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <div class="flex items-center gap-2 mb-3">
                <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                  </svg>
                </div>
                <h3 class="font-semibold text-slate-800">입금 계좌</h3>
              </div>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-slate-500">은행</span>
                  <span class="font-medium text-slate-800">{{ bankInfo.bankName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">계좌번호</span>
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-slate-800">{{ bankInfo.accountNumber }}</span>
                    <button
                      @click="copyAccountNumber"
                      class="p-1 hover:bg-slate-100 rounded transition"
                      title="복사"
                    >
                      <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-500">예금주</span>
                  <span class="font-medium text-slate-800">{{ bankInfo.accountHolder }}</span>
                </div>
              </div>
            </div>

            <!-- 충전 안내 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <div class="flex items-center gap-2 mb-3">
                <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <h3 class="font-semibold text-slate-800">충전 안내</h3>
              </div>
              <ul class="text-sm text-slate-600 space-y-2">
                <li class="flex items-start gap-2">
                  <span class="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                  <span>충전 신청 후 <strong class="text-blue-600">30분 이내</strong> 확인됩니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                  <span>입금자명과 신청자명이 일치해야 합니다</span>
                </li>
                <li class="flex items-start gap-2">
                  <span class="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></span>
                  <span>대량 충전 시 <strong class="text-green-600">최대 15% 보너스</strong> 지급</span>
                </li>
              </ul>
            </div>

            <!-- 바로가기 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 class="font-semibold text-slate-800 mb-3">바로가기</h3>
              <div class="space-y-2">
                <router-link
                  to="/partners/jump/purchase"
                  class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                >
                  <div class="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition">
                    <svg class="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-slate-700">현장 점프</p>
                    <p class="text-xs text-slate-500">상단 노출 구매</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
                <router-link
                  to="/partners/ad/create"
                  class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition group"
                >
                  <div class="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center group-hover:bg-violet-200 transition">
                    <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                    </svg>
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-slate-700">캠페인 만들기</p>
                    <p class="text-xs text-slate-500">전용 링크 생성</p>
                  </div>
                  <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 메인 콘텐츠 -->
          <div class="lg:col-span-2 space-y-6">
            <!-- 충전 패키지 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <h2 class="text-lg font-semibold text-slate-800 mb-4">충전 패키지</h2>
              <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <button
                  v-for="pkg in chargePackages"
                  :key="pkg.amount"
                  @click="selectPackage(pkg)"
                  class="p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition text-center group relative"
                >
                  <p v-if="pkg.bonusRate" class="absolute -top-2 -right-2 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                    +{{ pkg.bonusRate }}
                  </p>
                  <p class="text-xl font-bold text-slate-800 group-hover:text-blue-600">
                    {{ pkg.label }}
                  </p>
                  <p class="text-lg font-semibold text-blue-600 mt-1">
                    {{ pkg.price.toLocaleString() }}원
                  </p>
                  <p v-if="pkg.bonus" class="text-xs text-green-600 mt-1">
                    +{{ pkg.bonus.toLocaleString() }}P 보너스
                  </p>
                </button>
              </div>
            </div>

            <!-- 충전 신청 내역 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-semibold text-slate-800">충전 신청 내역</h2>
                <button
                  @click="loadMyChargeRequests"
                  class="text-sm text-blue-600 hover:text-blue-700"
                >
                  새로고침
                </button>
              </div>

              <div v-if="chargeRequests.length === 0" class="text-center py-8 text-slate-400">
                충전 신청 내역이 없습니다
              </div>

              <div v-else class="space-y-3">
                <div
                  v-for="req in chargeRequests"
                  :key="req.id"
                  class="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div class="flex items-center gap-3">
                    <div :class="[
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      req.status === 'pending' ? 'bg-amber-100' :
                      req.status === 'approved' ? 'bg-green-100' :
                      req.status === 'rejected' ? 'bg-red-100' : 'bg-slate-100'
                    ]">
                      <svg v-if="req.status === 'pending'" class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <svg v-else-if="req.status === 'approved'" class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <svg v-else-if="req.status === 'rejected'" class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                      <svg v-else class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </div>
                    <div>
                      <p class="font-medium text-slate-800">
                        {{ req.packagePrice?.toLocaleString() }}원 충전 신청
                        <span v-if="req.packageBonus" class="text-green-600 text-sm">(+{{ req.packageBonus.toLocaleString() }}P)</span>
                      </p>
                      <p class="text-sm text-slate-400">{{ formatDate(req.createdAt) }}</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <span :class="[
                      'px-3 py-1 text-xs font-medium rounded-full',
                      req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      req.status === 'approved' ? 'bg-green-100 text-green-700' :
                      req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                    ]">
                      {{ getStatusLabel(req.status) }}
                    </span>
                    <button
                      v-if="req.status === 'pending'"
                      @click="handleCancelRequest(req.id)"
                      class="block mt-2 text-xs text-red-500 hover:text-red-600"
                    >
                      신청 취소
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 포인트 사용 내역 -->
            <div class="bg-white rounded-2xl border border-slate-200 p-5">
              <h2 class="text-lg font-semibold text-slate-800 mb-4">포인트 사용 내역</h2>

              <div v-if="loading" class="flex justify-center py-8">
                <div class="spinner"></div>
              </div>

              <div v-else-if="transactions.length === 0" class="text-center py-8 text-slate-400">
                거래 내역이 없습니다
              </div>

              <div v-else class="space-y-3">
                <div
                  v-for="tx in transactions"
                  :key="tx.id"
                  class="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div class="flex items-center gap-3">
                    <div :class="[
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      tx.type === 'charge' ? 'bg-green-100' : tx.type === 'refund' ? 'bg-blue-100' : 'bg-red-100'
                    ]">
                      <svg v-if="tx.type === 'charge'" class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                      </svg>
                      <svg v-else-if="tx.type === 'refund'" class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                      </svg>
                      <svg v-else class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                      </svg>
                    </div>
                    <div>
                      <p class="font-medium text-slate-800">{{ tx.description }}</p>
                      <p class="text-sm text-slate-400">{{ formatDate(tx.createdAt) }}</p>
                    </div>
                  </div>
                  <div :class="[
                    'text-lg font-semibold',
                    tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                  ]">
                    {{ tx.amount > 0 ? '+' : '' }}{{ tx.amount.toLocaleString() }}P
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 충전 모달 -->
    <div v-if="showChargeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="showChargeModal = false">
      <div class="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
        <h3 class="text-xl font-bold text-slate-800 mb-4">포인트 충전 신청</h3>

        <div v-if="selectedPackage" class="bg-blue-50 rounded-xl p-4 mb-4">
          <div class="flex justify-between items-center">
            <span class="text-slate-600">충전 금액</span>
            <span class="font-bold text-blue-600">{{ selectedPackage.price.toLocaleString() }}원</span>
          </div>
          <div class="flex justify-between items-center mt-2">
            <span class="text-slate-600">적립 포인트</span>
            <span class="font-bold text-slate-800">{{ selectedPackage.amount.toLocaleString() }}P</span>
          </div>
          <div v-if="selectedPackage.bonus" class="flex justify-between items-center mt-2">
            <span class="text-slate-600">보너스</span>
            <span class="font-bold text-green-600">+{{ selectedPackage.bonus.toLocaleString() }}P</span>
          </div>
          <div class="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
            <span class="text-slate-600">총 적립</span>
            <span class="font-bold text-blue-700">{{ (selectedPackage.amount + (selectedPackage.bonus || 0)).toLocaleString() }}P</span>
          </div>
        </div>

        <!-- 입금자명 입력 -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-slate-700 mb-2">입금자명</label>
          <input
            v-model="depositorName"
            type="text"
            placeholder="실제 입금하실 분의 이름"
            class="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
          />
        </div>

        <!-- 계좌 정보 -->
        <div class="bg-amber-50 rounded-xl p-4 mb-4">
          <p class="text-sm font-medium text-amber-800 mb-2">입금 계좌</p>
          <p class="text-slate-700">{{ bankInfo.bankName }} {{ bankInfo.accountNumber }}</p>
          <p class="text-slate-700">{{ bankInfo.accountHolder }}</p>
        </div>

        <p class="text-sm text-slate-500 mb-4">
          * 위 계좌로 입금 후 30분 이내 확인됩니다.<br>
          * 입금자명과 신청자명이 일치해야 합니다.
        </p>

        <div class="flex gap-3">
          <button
            @click="showChargeModal = false"
            class="flex-1 py-3 border border-slate-200 rounded-xl font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            취소
          </button>
          <button
            @click="handleChargeRequest"
            :disabled="charging || !depositorName.trim()"
            class="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50"
          >
            {{ charging ? '처리 중...' : '충전 신청' }}
          </button>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, onMounted } from 'vue'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { usePoint, chargePackages, bankInfo } from '@/composables/partners/usePoint'

export default {
  name: 'PointManageView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const {
      balance,
      transactions,
      chargeRequests,
      loading,
      loadBalance,
      loadTransactions,
      loadMyChargeRequests,
      requestCharge,
      cancelChargeRequest
    } = usePoint()

    const showChargeModal = ref(false)
    const selectedPackage = ref(null)
    const depositorName = ref('')
    const charging = ref(false)

    const selectPackage = (pkg) => {
      selectedPackage.value = pkg
      showChargeModal.value = true
    }

    const handleChargeRequest = async () => {
      if (!selectedPackage.value || charging.value || !depositorName.value.trim()) return

      charging.value = true

      const result = await requestCharge(selectedPackage.value, depositorName.value.trim())

      charging.value = false

      if (result.success) {
        showChargeModal.value = false
        selectedPackage.value = null
        depositorName.value = ''
        await loadMyChargeRequests()
        alert('충전 신청이 완료되었습니다.\n입금 확인 후 30분 이내 포인트가 적립됩니다.')
      } else {
        alert('충전 신청 실패: ' + result.error)
      }
    }

    const handleCancelRequest = async (requestId) => {
      if (!confirm('충전 신청을 취소하시겠습니까?')) return

      const result = await cancelChargeRequest(requestId)

      if (result.success) {
        await loadMyChargeRequests()
        alert('신청이 취소되었습니다.')
      } else {
        alert('취소 실패: ' + result.error)
      }
    }

    const copyAccountNumber = () => {
      navigator.clipboard.writeText(bankInfo.accountNumber.replace(/-/g, ''))
      alert('계좌번호가 복사되었습니다.')
    }

    const getStatusLabel = (status) => {
      switch (status) {
        case 'pending': return '대기 중'
        case 'approved': return '승인 완료'
        case 'rejected': return '거절됨'
        case 'cancelled': return '취소됨'
        default: return status
      }
    }

    const formatDate = (dateStr) => {
      const date = new Date(dateStr)
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(async () => {
      await loadBalance()
      await loadTransactions()
      await loadMyChargeRequests()
    })

    return {
      balance,
      transactions,
      chargeRequests,
      loading,
      showChargeModal,
      selectedPackage,
      depositorName,
      charging,
      chargePackages,
      bankInfo,
      selectPackage,
      handleChargeRequest,
      handleCancelRequest,
      copyAccountNumber,
      getStatusLabel,
      formatDate,
      loadMyChargeRequests
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";

.spinner {
  @apply w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin;
}
</style>
